import { Button, Form, FormCard, FormItem, PageHeader } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { Form as AntdForm, Input, Spin } from 'antd'
import { PageWrapper } from '../components'
import { CheckOutlined } from '@ant-design/icons'
import { useState } from 'react'
import styled from 'styled-components'
import { showToast } from 'common/utils'
import { useSelector } from 'react-redux'
import { HomeDto } from '@lmc/ts-common'
import { handleOnFinishFailed } from '../formComponents/utils'
import { useWatch } from 'antd/lib/form/Form'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { Text } from '@lmc/ui-kit/src/general'
import useUpdateWarningsSettings from '../hooks/useUpdateWarningsSettings'
import useFeatureFlags from 'hooks/useFeatureFlags'
import SettingsPaywall from './components/SettingsPaywall'
import { Navigate } from 'react-router-dom'

type WarningsSettingsForm = {
  durationMismatched: number
  clockOutEarly: number
  clockOutLate: number
  clockInEarly: number
  clockInLate: number
  missed: number
}

const WarningsSettingsScreen = () => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const updateWarningsSettings = useUpdateWarningsSettings()
  const { data: flags, isLoading } = useFeatureFlags({
    flagNames: ['rostering_tolerance_settings'],
  })

  const flagsRosteringToleranceSettings =
    flags?.rostering_tolerance_settings?.enabled

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home,
  )

  const initialValues = home.rotaSettings?.warningsTolerance

  useWatch('warnings', form)

  const handleOnFinish = async (values: WarningsSettingsForm) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)

      await updateWarningsSettings({
        warningsTolerance: {
          clockInLate: Number(values.clockInLate),
          clockInEarly: Number(values.clockInEarly),
          clockOutEarly: Number(values.clockOutEarly),
          clockOutLate: Number(values.clockOutLate),
          durationMismatched: Number(values.durationMismatched),
          missed: Number(values.missed),
        },
      })

      setIsUpdateLoading(false)
    } catch (err: any) {
      showToast({
        title: err?.message || t('SETTINGS_ERROR'),
        type: 'error',
      })

      setIsUpdateLoading(false)
    }
    setIsDirty(false)
  }

  const handleFormValuesChange = (values: WarningsSettingsForm) => {
    if (values) {
      setIsDirty(true)
    }
  }

  if (!flagsRosteringToleranceSettings && !isLoading) return <Navigate to="/" />

  return (
    <>
      <PageHeader
        title={t('WARNINGS')}
        subtitle={t('WARNINGS_SETTINGS_INFO')}
        sticky
      />
      <PageWrapper>
        <SettingsPaywall startingText={t('WARNINGS_IS')}>
          <Form<WarningsSettingsForm>
            form={form}
            initialValues={initialValues}
            onValuesChange={handleFormValuesChange}
            onFinish={handleOnFinish}
            onFinishFailed={({ errorFields }) => {
              handleOnFinishFailed(errorFields)
            }}
          >
            <FormCard title={t('TOLERANCE_SETTINGS')}>
              <DividerSpace />
              <FormItem label={`${t('DURATION_MISMATCH')}: `}>
                <FormItem name="durationMismatched">
                  <Input type="number" suffix={t('MINUTES')} />
                </FormItem>
                <GrayText>{t('DURATION_MISMATCH_INFO')}</GrayText>
              </FormItem>
              <FormItem label={`${t('CLOCKED_OUT_EARLY')}: `}>
                <FormItem name="clockOutEarly">
                  <Input type="number" suffix={t('MINUTES')} />
                </FormItem>
                <GrayText>{t('CLOCKED_OUT_EARLY_INFO')}</GrayText>
              </FormItem>
              <FormItem label={`${t('CLOCKED_OUT_LATE')}: `}>
                <FormItem name="clockOutLate">
                  <Input type="number" suffix={t('MINUTES')} />
                </FormItem>
                <GrayText>{t('CLOCKED_OUT_LATE_INFO')}</GrayText>
              </FormItem>
              <FormItem label={`${t('CLOCKED_IN_EARLY')}: `}>
                <FormItem name="clockInEarly">
                  <Input type="number" suffix={t('MINUTES')} />
                </FormItem>
                <GrayText>{t('CLOCKED_IN_EARLY_INFO')}</GrayText>
              </FormItem>
              <FormItem label={`${t('CLOCKED_IN_LATE')}: `}>
                <FormItem name="clockInLate">
                  <Input type="number" suffix={t('MINUTES')} />
                </FormItem>
                <GrayText>{t('CLOCKED_IN_LATE_INFO')}</GrayText>
              </FormItem>
              <FormItem label={`${t('MISSED')}: `}>
                <FormItem name="missed">
                  <Input type="number" suffix={t('MINUTES')} />
                </FormItem>
                <GrayText>{t('MISSED_INFO')}</GrayText>
              </FormItem>
            </FormCard>
            <FixedFooter>
              <Button
                isLoading={isUpdateLoading}
                variant="primary"
                type="submit"
                disabled={!isDirty}
                onClick={form.submit}
              >
                <CheckOutlined />
                {t('SAVE_CHANGES')}
              </Button>
            </FixedFooter>
          </Form>
        </SettingsPaywall>
      </PageWrapper>
      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('ROTA_SETTINGS_NAVIGATION_MODAL_CONTENT')}
      />
    </>
  )
}

const FixedFooter = styled.div.attrs({
  className: 'lmc-fixed-footer', // I don't like this, we need to make a SB component after the removal of the keystone legacy file
})`
  border: none;
  display: flex;
  justify-content: flex-end;
  width: calc(100vw - 290px);
  @media (max-width: 768px) {
    width: 100vw;
  }
`

const GrayText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const DividerSpace = styled.div``

export default WarningsSettingsScreen
